/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useContext, useEffect, useState, memo, type RefObject } from 'react';
import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { isFakeId } from '@jira/polaris__component-navigation-store/src/updateContainerState.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import { useNavigationView } from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import { useManageViewPermissions } from '@jira/polaris__component-permissions-store/src/useCanEditIssues.tsx';
import { useEmojiProviderPromise } from '@jira/polaris__lib-emoji-picker/src/useEmoji.tsx';
import type { EmojiId } from '@atlaskit/emoji/types';
import { EmojiPicker } from '@atlaskit/emoji';
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { cssMap, cx } from '@atlaskit/css';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { WithDragPreview } from '../../../../drag-preview/index.tsx';
import { EditableTitle } from '../../../../editable-title/index.tsx';
import { TitleIconResolver, ViewIcon } from '../../../../title-icon-resolver/index.tsx';
import { DraggableRefContext } from '../../../../../common/utils/draggable-context/index.tsx';
import {
	useAnalyticsL2,
	useGetViewUrl,
	useContainerTypePermissions,
	useIsReadOnly,
	useContainerId,
} from '../../../../../controllers/context.tsx';
import type { NavigationContainer } from '../../../../../common/types.tsx';
import { useIsViewKindLocked } from '../../../../../common/utils/useIsViewKindLocked.tsx';
import { Menu } from './menu/index.tsx';

const defaultTitle = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.default-title',
	defaultMessage: 'Untitled View',
	description: 'Header text to be used as a default when a view title changes to empty',
});

const nameLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.rename-label',
	defaultMessage: 'View name',
	description: 'Accessibility label display for input when putting view name in navigation',
});

const menuItemEmojiPicker = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu-item-emoji-picker',
	defaultMessage: 'Open emoji picker for {viewName}',
	description: 'Accessible label for the emoji picker button',
});

type ViewItemProps = {
	localId: LocalViewId;
	isDraggingPreview?: boolean;
	navigationContainer: NavigationContainer;
};

const styles = cssMap({
	emojiButton: {
		backgroundColor: 'transparent',
	},
	emojiButtonDisabled: {
		pointerEvents: 'none',
	},
});

// oxlint-disable-next-line typescript/no-invalid-void-type
export const useRoutePath: (args_0: void) => string = createRouterSelector(
	(state) => state.location.pathname,
);

export const ViewItem: React.MemoExoticComponent<
	({ localId, isDraggingPreview, navigationContainer }: ViewItemProps) => React.JSX.Element | null
> = memo(
	({
		localId,
		isDraggingPreview,
		navigationContainer,
	}: ViewItemProps): React.JSX.Element | null => {
		const containerId = useContainerId();
		const getViewUrl = useGetViewUrl();
		const [isPickerOpen, setPickerOpen] = useState(false);
		const emojiProviderPromise = useEmojiProviderPromise();
		const { formatMessage } = useIntl();
		const [view] = useNavigationView({ localId, containerId });
		const pathname = useRoutePath();
		const draggableRef = useContext(DraggableRefContext);
		const containerTypePermissions = useContainerTypePermissions();
		const isReadOnly = useIsReadOnly();
		const { canManageView, canSeeView, loadViewPermissionsForCurrentUser } =
			useManageViewPermissions(containerTypePermissions === 'view' ? view?.id : containerId);
		const { sendViewNavigationItemInsideProjectsAnalyticsEvent } = useAnalyticsL2();
		const isViewKindLocked = useIsViewKindLocked();
		const isGlobalView = Boolean(view?.global) && fg('polaris_global_views');

		const { updateView, deleteViewFromState, cancelViewRenaming, addEmoji } =
			useNavigationActions();

		const handleTitleChanged = useCallback(
			(newTitle: string) => {
				if (!view) {
					return;
				}
				updateView(view.localId, newTitle || formatMessage(defaultTitle));
			},
			[view, formatMessage, updateView],
		);

		const handleTitleChangeCanceled = useCallback(() => {
			if (!view) {
				return;
			}
			cancelViewRenaming(view.localId);
		}, [view, cancelViewRenaming]);

		const handleEmojiIconClick = useCallback(
			(e: React.MouseEvent) => {
				if (fg('jpd_tree-view')) {
					if (!canManageView || isReadOnly || (view?.viewType && isViewKindLocked(view.viewType))) {
						return;
					}
				} else if (!canManageView || isReadOnly) {
					return;
				}
				e.preventDefault();
				e.stopPropagation();
				setPickerOpen(!isPickerOpen);
			},
			[canManageView, isPickerOpen, isReadOnly, isViewKindLocked, view],
		);

		const handleSelectEmoji = useCallback(
			(emoji: EmojiId) => {
				setPickerOpen(false);
				if (!view) {
					return;
				}
				addEmoji(emoji.id, view.localId);
			},
			[view, setPickerOpen, addEmoji],
		);

		const handleTitleIconHover = useCallback(() => {
			if (view && !isFakeId(view.id)) {
				loadViewPermissionsForCurrentUser(
					containerTypePermissions === 'view' ? view.id : containerId,
				);
			}
		}, [containerId, containerTypePermissions, loadViewPermissionsForCurrentUser, view]);

		const getEmojiPicker = useCallback(
			() => <EmojiPicker emojiProvider={emojiProviderPromise} onSelection={handleSelectEmoji} />,
			[emojiProviderPromise, handleSelectEmoji],
		);

		const handleClick = useCallback(() => {
			sendViewNavigationItemInsideProjectsAnalyticsEvent({
				container: navigationContainer,
				viewParams: {
					viewId: view?.id,
					viewKind: view?.viewType,
				},
			});
		}, [
			sendViewNavigationItemInsideProjectsAnalyticsEvent,
			navigationContainer,
			view?.id,
			view?.viewType,
		]);

		useEffect(() => {
			if (view && !canSeeView) {
				deleteViewFromState(view.id);
			}
		}, [view, canSeeView, deleteViewFromState]);

		if (!view) {
			return null;
		}

		// The view is in an editing state if the user is renaming it or if the view was just created and has no name
		const isEditing = view.isRenaming || view.name === '';
		if (isEditing) {
			return (
				<MenuListItem>
					<EditableTitle
						label={formatMessage(nameLabel)}
						value={view.name}
						elemBefore={<ViewIcon viewKind={view.viewType} emojiId={view.emoji} />}
						onChanged={handleTitleChanged}
						onCancel={handleTitleChangeCanceled}
					/>
				</MenuListItem>
			);
		}

		const url = getViewUrl({ container: navigationContainer, view });
		const isSelected = pathname === url;

		return (
			<WithDragPreview isEnabled={isDraggingPreview || false}>
				<MenuList>
					<LinkMenuItem
						// oxlint-disable-next-line typescript/consistent-type-assertions
						ref={draggableRef as RefObject<HTMLAnchorElement>}
						isSelected={isSelected}
						href={url}
						elemBefore={
							<Popup
								placement="auto-start"
								isOpen={isPickerOpen}
								shouldUseCaptureOnOutsideClick
								onClose={() => setPickerOpen(false)}
								content={getEmojiPicker}
								// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
								messageId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.menu-items.views.viewset.popup"
								messageType="transactional"
								trigger={(triggerProps) =>
									fg('polaris_global_views') ? (
										<Pressable
											{...triggerProps}
											onClick={handleEmojiIconClick}
											onMouseEnter={handleTitleIconHover}
											isDisabled={isGlobalView && fg('polaris_global_views')}
											padding="space.0"
											xcss={cx(styles.emojiButton, isGlobalView && styles.emojiButtonDisabled)}
											aria-label={formatMessage(menuItemEmojiPicker, {
												viewName: view.name,
											})}
										>
											<TitleIconResolver
												emojiId={view.emoji}
												viewId={containerTypePermissions === 'project' ? containerId : view.id}
												viewKind={view.viewType}
												isSelected={isSelected}
											/>
										</Pressable>
									) : (
										<Pressable
											{...triggerProps}
											onClick={handleEmojiIconClick}
											onMouseEnter={handleTitleIconHover}
											padding="space.0"
											xcss={stylesOld}
											aria-label={formatMessage(menuItemEmojiPicker, {
												viewName: view.name,
											})}
										>
											<TitleIconResolver
												emojiId={view.emoji}
												viewId={containerTypePermissions === 'project' ? containerId : view.id}
												viewKind={view.viewType}
												isSelected={isSelected}
											/>
										</Pressable>
									)
								}
							/>
						}
						actionsOnHover={<Menu view={view} navigationContainer={navigationContainer} />}
						onClick={handleClick}
					>
						{view.name}
					</LinkMenuItem>
				</MenuList>
			</WithDragPreview>
		);
	},
);

const stylesOld = xcss({ background: 'transparent' });
